import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../theme';
import type { Palette } from '../theme';
import Txt from './Txt';

type ChipVariant = 'default' | 'accent' | 'success' | 'warn' | 'danger' | 'info' | 'neutral' | 'solid' | 'outline';

interface ChipProps {
  variant?: ChipVariant;
  children: React.ReactNode;
  dot?: boolean;
  lg?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** RN port of theme.css's .t-chip — pill badge, semantic soft variants, optional leading dot. */
export default function Chip({ variant = 'default', children, dot, lg, style }: ChipProps) {
  const t = useTheme();
  const c = t.colors;

  // [background, borderColor, textColor, dotColor]
  const map: Record<ChipVariant, [string, string, string, string]> = {
    default: [c.card, c.line, c.ink2, c.ink3],
    accent: [c.accentSoft, c.accentLine, c.accentInk, c.accent],
    success: [c.successSoft, c.successLine, c.success, c.success],
    warn: [c.warnSoft, c.warnLine, c.warn, c.warn],
    danger: [c.dangerSoft, c.dangerLine, c.danger, c.danger],
    info: [c.infoSoft, c.infoLine, c.info, c.info],
    neutral: [c.neutralSoft, c.neutralLine, c.neutral, c.neutral],
    solid: [c.ink, c.ink, c.paper, c.paper],
    outline: ['transparent', c.line, c.ink2, c.ink3],
  };
  const [bg, border, fg, dotColor] = map[variant];

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          height: lg ? 26 : 22,
          paddingHorizontal: lg ? 12 : 9,
          borderRadius: t.radii.full,
          borderWidth: 1,
          borderColor: border,
          backgroundColor: bg,
          alignSelf: 'flex-start',
        },
        style,
      ]}
    >
      {dot && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: dotColor }} />}
      {typeof children === 'string'
        ? <Txt size={lg ? 'sm' : 'xs'} weight="medium" color={fg}>{children}</Txt>
        : children}
    </View>
  );
}

export type { ChipVariant };
export type { Palette };
